import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';

export interface AppDefinition {
  id: string;
  name: string;
  slug: string;
  type: 'core' | 'addon' | 'powerup';
  category: string;
  icon: string;
  route_prefix: string;
  settings_route: string;
  parent_app_id: string | null;
}

export interface InstalledApp {
  id: string;
  business_id: string;
  app_id: string;
  status: 'active' | 'inactive' | 'trial';
  settings: any;
  visible_app_grid?: number;
  hidden_app_grid?: number;
  app: AppDefinition;
}

export function useAppSystem() {
  const businessContext = useBusiness();
  const selectedBusiness = businessContext?.selectedBusiness;
  
  const [isUsingAppSystem, setIsUsingAppSystem] = useState(false);
  const [coreApps, setCoreApps] = useState<InstalledApp[]>([]);
  const [installedAddons, setInstalledAddons] = useState<InstalledApp[]>([]);
  const [loading, setLoading] = useState(false); // Start with false to prevent blank screen
  const [refreshKey, setRefreshKey] = useState(0); // Force refresh key

  useEffect(() => {
    if (!selectedBusiness || !selectedBusiness.id) {
      setLoading(false);
      setIsUsingAppSystem(false);
      return;
    }

    // Only reload if the business ID actually changed, not just the object reference
    const businessId = selectedBusiness.id;
    loadAppSystem();
  }, [selectedBusiness?.id]); // Use selectedBusiness?.id instead of selectedBusiness to prevent unnecessary reloads

  const loadAppSystem = async () => {
    try {
      console.log('🔄 useAppSystem: Loading app system...');
      setLoading(true);

      // Check if supabase is available
      if (!supabase) {
        console.error('Supabase client not available');
        setLoading(false);
        return;
      }

      // FORCE ALL BUSINESSES TO USE APP SYSTEM
      console.log('🚀 useAppSystem: Forcing app system enabled for all businesses');
      setIsUsingAppSystem(true);

      // Load installed apps for this business
      console.log('Loading apps for business:', selectedBusiness.id);
      const { data: installedApps, error: appsError } = await supabase
        .from('business_apps_v2')
        .select(`
          *,
          app:app_registry_v2(*)
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'active')
        .order('visible_app_grid', { ascending: true, nullsLast: true });

      if (appsError) {
        console.error('Error loading installed apps:', appsError);
        setLoading(false);
        return;
      }
      
      console.log('Installed apps found:', installedApps);

      // If no apps are installed, log a warning but don't auto-install
      // (Core apps should be installed via migration)
      if (!installedApps || installedApps.length === 0) {
        console.log('⚠️ No apps found for business - this should not happen after migration');
        setCoreApps([]);
        setInstalledAddons([]);
        setLoading(false);
        return;
      }

      // Separate core apps from addons
      const core = installedApps?.filter(app => app.app?.type === 'core') || [];
      const addons = installedApps?.filter(app => app.app?.type === 'addon') || [];

      setCoreApps(core);
      setInstalledAddons(addons);
      setLoading(false);
      // Increment refresh key to force component re-renders
      setRefreshKey(prev => prev + 1);
      console.log('✅ useAppSystem: App system loaded successfully', { coreCount: core.length, addonCount: addons.length, refreshKey: refreshKey + 1 });
    } catch (error) {
      console.error('Error in loadAppSystem:', error);
      setLoading(false);
    }
  };

  const forceReload = async () => {
    console.log('🔄 useAppSystem: Force reloading app system...');
    // Clear current state first to force a fresh load
    setCoreApps([]);
    setInstalledAddons([]);
    setLoading(true);
    // Add a small delay to ensure state is cleared
    await new Promise(resolve => setTimeout(resolve, 100));
    await loadAppSystem();
  };

  return {
    isUsingAppSystem,
    coreApps,
    installedAddons,
    loading,
    refreshKey, // Expose refresh key for components that need to force re-render
    reload: forceReload
  };
}
